import React from "react";
import {
  Menu,
  MenuItem,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import SouthIcon from "@mui/icons-material/South";
import NorthIcon from "@mui/icons-material/North";
import Cell from "src/Tables/TableTemplate/Cell";
import { ITableUI } from "src/Tables/TableTemplate/tableTemplate";
import { Header, ITableReport } from "src/Interfaces/TableInterfaces";

const headerCellClassName =
  "!cursor-default bg-white !font-black text-black !text-center";

const sortableHeaderCellClassName =
  "!cursor-pointer bg-white hover:!bg-slate-200 !transition-all !font-black text-black !text-center";

const tableCellClassName = "whitespace-nowrap overflow-hidden text-ellipsis";

const dynamicCellClassName = "!w-[120px] !min-w-[120px] !max-w-[120px]";

const getDynamicCellWidthClassName = (header: Header) => {
  if (!header.headerName.startsWith("limitDeviation_"))
    return dynamicCellClassName;

  if (
    header.headerName.endsWith("_engagedCapital") ||
    header.headerName.endsWith("_surplusDeficitValue")
  )
    return "!w-[170px] !min-w-[170px] !max-w-[170px]";

  return "!w-[95px] !min-w-[95px] !max-w-[95px]";
};

const getGroupBorderClassName = (headers: Header[], index: number) => {
  const isGroupStart =
    index === 0 || headers[index - 1].groupName !== headers[index].groupName;
  const isLastColumn = index === headers.length - 1;

  return `${isGroupStart ? "!border-r-2 !border-r-sky-200" : ""} ${
    isLastColumn ? "!border-l-2 !border-l-sky-200" : ""
  }`;
};

const groupDynamicHeaders = (headers: Header[]) => {
  const groups: { groupName: string; headers: Header[] }[] = [];

  headers.forEach((header) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.groupName === header.groupName) {
      lastGroup.headers.push(header);
      return;
    }

    groups.push({ groupName: header.groupName!, headers: [header] });
  });

  return groups;
};

const filterDynamicHeaders = (headers: Header[], showHiddenCols?: boolean) => {
  if (showHiddenCols == null) return headers;

  return headers.filter((header) =>
    showHiddenCols
      ? header.positionTypeStr === "FirstPage" ||
        header.positionTypeStr === "DefaultHidden"
      : header.positionTypeStr === "FirstPage",
  );
};

const getImbalanceCellClassName = (row: ITableReport, header: Header) => {
  const suffix = header.headerName.endsWith("_imbalance")
    ? "_imbalance"
    : header.headerName.endsWith("_requiredAction")
      ? "_requiredAction"
      : null;

  if (!suffix) return "";

  const imbalanceColumnName = `${header.headerName.slice(0, -suffix.length)}_imbalance`;
  const imbalance = Number(
    (row as unknown as Record<string, unknown>)[imbalanceColumnName],
  );

  if (!Number.isFinite(imbalance) || imbalance === 0) return "";

  return imbalance > 0 ? "text-red-600" : "text-green-600";
};

const getFrozenStaticHeaders = (headers: Header[]) => {
  const staticHeaders = headers.filter((header) => !header.groupName);
  const markedFixedHeaders = staticHeaders.filter(
    (header) => header.positionTypeStr === "FixedFirst",
  );

  return (markedFixedHeaders.length ? markedFixedHeaders : staticHeaders).slice(
    0,
    5,
  );
};

const renderHeaderText = (text?: string | null) => {
  const lines = text?.split("\n") ?? [];
  if (lines.length <= 1) return <span>{text}</span>;

  return (
    <span className="leading-5">
      <span className="block">{lines[0]}</span>
      {lines.slice(1).map((line, index) => (
        <span
          key={`${line}-${index}`}
          className="block !font-normal text-[10px] leading-4"
        >
          {line}
        </span>
      ))}
    </span>
  );
};

const AllocationTableUI = <TRow extends ITableReport>({
  reports,
  onAction,
  tableBoxClassName,
  sortingType,
  setSortingType,
  onTableRowClick,
  isRowClickable,
  onTableHeaderClick,
  showHiddenCols,
  onCellClick,
  isCellActive,
  activeRowId,
}: ITableUI<TRow>) => {
  const firstHeaderRowRef = React.useRef<HTMLTableRowElement>(null);
  const fixedFirstHeaderCellRefs = React.useRef<
    Record<string, HTMLTableCellElement | null>
  >({});
  const [firstRowHeight, setFirstRowHeight] = React.useState(0);
  const [fixedFirstRightOffsets, setFixedFirstRightOffsets] = React.useState<
    Record<string, number>
  >({});
  const [segregationModal, setSegregationModal] = React.useState<{
    accountId: number;
    tag: string;
  } | null>(null);
  const [contextMenuAnchor, setContextMenuAnchor] =
    React.useState<HTMLElement | null>(null);
  const [contextMenuData, setContextMenuData] = React.useState<{
    accountId: number;
    tag: string;
  } | null>(null);
  const [selectedRowId, setSelectedRowId] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    const row = firstHeaderRowRef.current;
    if (!row) return;

    const updateHeight = () => {
      setFirstRowHeight(row.getBoundingClientRect().height);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(row);
    return () => observer.disconnect();
  }, [reports]);

  React.useLayoutEffect(() => {
    if (!reports) return;

    const updateOffsets = () => {
      const fixedFirst = getFrozenStaticHeaders(
        reports.translatedHeaders ?? [],
      );
      const offsets: Record<string, number> = {};
      let accumulatedRight = 0;

      fixedFirst.forEach((header) => {
        offsets[header.headerName] = accumulatedRight;
        const cell = fixedFirstHeaderCellRefs.current[header.headerName];
        accumulatedRight += cell?.getBoundingClientRect().width ?? 0;
      });

      setFixedFirstRightOffsets(offsets);
    };

    updateOffsets();
    const row = firstHeaderRowRef.current;
    if (!row) return;

    const observer = new ResizeObserver(updateOffsets);
    observer.observe(row);
    return () => observer.disconnect();
  }, [reports]);

  if (!reports) return null;

  const highlightedRowId = activeRowId ?? selectedRowId;

  const selectRow = (row: TRow) => {
    if (isRowClickable && !isRowClickable(row)) return;
    setSelectedRowId(row.id);
  };

  const rowClickHandler = (row: TRow) => {
    selectRow(row);
    onTableRowClick?.(row);
  };

  const headers = reports.translatedHeaders ?? [];
  const staticHeaders = headers.filter((header) => !header.groupName);
  const fixedFirstHeaders = getFrozenStaticHeaders(headers);
  const fixedFirstHeaderNames = new Set(
    fixedFirstHeaders.map((header) => header.headerName),
  );
  const scrollableStaticHeaders = staticHeaders.filter(
    (header) => !fixedFirstHeaderNames.has(header.headerName),
  );
  const dynamicHeaders = filterDynamicHeaders(
    headers.filter((header) => header.groupName),
    showHiddenCols,
  );
  const dynamicGroups = groupDynamicHeaders(dynamicHeaders);

  const getFixedFirstInnerCellClassName = (header: Header) =>
    header.translateHeaderName === "#" ? "" : "inner-fixed-column";

  const getFixedFirstRightOffset = (header: Header) =>
    fixedFirstRightOffsets[header.headerName] ?? 0;

  const tableHeaderClickHandler = (header: Header) => {
    if ((sortingType || sortingType === "") && setSortingType)
      setSortingType(header.headerName);
    if (onTableHeaderClick) onTableHeaderClick(header);
  };

  const handleCloseContextMenu = () => {
    setContextMenuAnchor(null);
    setContextMenuData(null);
  };

  const dynamicCellRightClickHandler = (
    e: React.MouseEvent<HTMLElement>,
    row: TRow,
    header: Header,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const tag = header.tag != null && header.tag !== "" ? header.tag : null;
    if (tag == null || Number.isNaN(tag)) return;
    setContextMenuAnchor(e.currentTarget);
    setContextMenuData({ accountId: row.id, tag: tag as string });
  };

  const detailsClickHandler = () => {
    if (contextMenuData) setSegregationModal(contextMenuData);
    handleCloseContextMenu();
  };

  const getHeadersSortingIcon = (theader: Header) => {
    if (!sortingType) return null;

    const sortingTypeParts = sortingType.split("_");
    const sortingTypeHeaderName = sortingTypeParts.slice(0, -1).join("_");
    if (theader.headerName === sortingTypeHeaderName) {
      if (sortingTypeParts[sortingTypeParts.length - 1] === "desc")
        return <SouthIcon className="!text-xs" />;
      if (sortingTypeParts[sortingTypeParts.length - 1] === "asc")
        return <NorthIcon className="!text-xs" />;
    }
  };

  const getTableHeaderJSX = (theader: Header, className?: string) => (
    <TableCell
      key={theader.headerName}
      rowSpan={dynamicHeaders.length ? 5 : undefined}
      style={{ top: 0 }}
      className={`${sortableHeaderCellClassName} !sticky !z-30 ${theader.classname ?? ""} ${className ?? ""}`}
      onClick={() => tableHeaderClickHandler(theader)}
    >
      {renderHeaderText(theader.translateHeaderName)}
      {getHeadersSortingIcon(theader)}
    </TableCell>
  );

  const renderHeaderRows = () => (
    <>
      <TableRow ref={firstHeaderRowRef}>
        {fixedFirstHeaders.map((header) => (
          <TableCell
            key={header.headerName}
            ref={(el) => {
              fixedFirstHeaderCellRefs.current[header.headerName] =
                el as HTMLTableCellElement | null;
            }}
            rowSpan={dynamicHeaders.length ? 5 : undefined}
            style={{ right: getFixedFirstRightOffset(header), top: 0 }}
            className={`${sortableHeaderCellClassName} !sticky !z-[100] !bg-sky-50 fixed-column ${getFixedFirstInnerCellClassName(header)} ${header.classname ?? ""}`}
            onClick={() => tableHeaderClickHandler(header)}
          >
            {renderHeaderText(header.translateHeaderName)}
            {getHeadersSortingIcon(header)}
          </TableCell>
        ))}
        {scrollableStaticHeaders.map((header) => getTableHeaderJSX(header))}
        {dynamicGroups.map((group, index) => (
          <TableCell
            key={group.groupName}
            colSpan={group.headers.length}
            style={{ top: 0 }}
            className={`${headerCellClassName} !sticky !z-30 ${index === 0 ? "!border-r-2 !border-r-sky-200 !border-l-2 !border-b-2 !border-b-sky-200 !border-l-sky-200" : ""} ${index > 0 ? "!border-l-2 !border-b-2 !border-b-sky-200 !border-l-sky-200" : ""}`}
          >
            {renderHeaderText(group.groupName)}
          </TableCell>
        ))}
      </TableRow>
      {dynamicHeaders.length ? (
        <TableRow>
          {dynamicHeaders.map((header, index) => (
            <TableCell
              key={header.headerName}
              style={{ top: firstRowHeight }}
              className={`${sortableHeaderCellClassName} ${getDynamicCellWidthClassName(header)} ${header.classname ?? ""} ${getGroupBorderClassName(dynamicHeaders, index)}`}
              onClick={() => tableHeaderClickHandler(header)}
            >
              {renderHeaderText(header.translateHeaderName)}
              {getHeadersSortingIcon(header)}
            </TableCell>
          ))}
        </TableRow>
      ) : null}
    </>
  );

  const renderBody = () => {
    if (!headers.length || !reports.report.length) {
      return (
        <TableRow className="!relative h-10 !w-full">
          <TableCell className="!w-full !m-auto !flex !justify-center !items-center !h-full !absolute">
            موردی یافت نشد
          </TableCell>
        </TableRow>
      );
    }

    return reports.report.map((row) => {
      const clonedRow = JSON.parse(JSON.stringify(row));
      const isActiveRow = highlightedRowId === row.id;
      const isClickableRow = !isRowClickable || isRowClickable(row);

      return (
        <TableRow
          key={row.row}
          className={`group portfolio-allocation-row !transition-all !w-full !relative ${
            isActiveRow ? "!bg-blue-200" : "hover:!bg-slate-100"
          } ${isClickableRow ? "cursor-pointer" : "cursor-default"}`}
          onClick={isClickableRow ? () => rowClickHandler(row) : undefined}
        >
          {fixedFirstHeaders.map((header) => (
            <TableCell
              key={`${header.headerName}-${row.row}`}
              style={{ right: getFixedFirstRightOffset(header) }}
              className={`!sticky !z-50 ${isActiveRow ? "!bg-blue-200" : "!bg-sky-50 group-hover:!bg-slate-100"} fixed-column ${getFixedFirstInnerCellClassName(header)}`}
            >
              <div className="flex flex-row justify-center items-center gap-2 !w-full">
                <Cell
                  onAction={onAction}
                  clonedRow={clonedRow}
                  headerName={header.headerName}
                  type={header.typeStr}
                />
              </div>
            </TableCell>
          ))}
          {scrollableStaticHeaders.map((header) => (
            <TableCell key={`${header.headerName}-${row.row}`}>
              <div className="flex flex-row justify-center items-center gap-2 !w-full">
                <Cell
                  onAction={onAction}
                  clonedRow={clonedRow}
                  headerName={header.headerName}
                  type={header.typeStr}
                />
              </div>
            </TableCell>
          ))}
          {dynamicHeaders.map((header, index) => (
            <TableCell
              key={`${header.headerName}-${row.row}`}
              className={`${tableCellClassName} ${getDynamicCellWidthClassName(header)} ${
                isCellActive?.(row, header) ? "!bg-blue-300 !font-bold" : ""
              } ${getGroupBorderClassName(
                dynamicHeaders,
                index,
              )}`}
              onClick={
                onCellClick
                  ? (event) => {
                      event.stopPropagation();
                      selectRow(row);
                      onCellClick(row, header);
                    }
                  : undefined
              }
            >
              <div
                className={`flex flex-row justify-center items-center gap-2 !w-full ${
                  onCellClick ? "cursor-pointer" : ""
                } ${getImbalanceCellClassName(
                  row,
                  header,
                )}`}
                onContextMenu={(e) =>
                  dynamicCellRightClickHandler(e, row, header)
                }
              >
                <Cell
                  onAction={onAction}
                  clonedRow={clonedRow}
                  headerName={header.headerName}
                  type={header.typeStr}
                />
              </div>
            </TableCell>
          ))}
        </TableRow>
      );
    });
  };

  const multiRowStickyHeaderSx = {
    "& .MuiTableCell-head": {
      backgroundColor: "#fff",
      position: "sticky",
    },
    "& thead tr:first-of-type .MuiTableCell-head": {
      top: 0,
      zIndex: 30,
    },
    "& thead tr:nth-of-type(5) .MuiTableCell-head": {
      top: firstRowHeight,
      zIndex: 20,
    },
  };

  return (
    <>
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={handleCloseContextMenu}
      >
        <MenuItem onClick={detailsClickHandler}>جزئیات</MenuItem>
      </Menu>

      <div className={`w-full overflow-auto ${tableBoxClassName ?? ""}`}>
        <div className="portfolio-allocation-table w-fit mx-auto border-2 border-sky-100 rounded">
          <MuiTable
            stickyHeader
            className="relative min-h-[100px] !w-max"
            sx={multiRowStickyHeaderSx}
          >
            <TableHead>{renderHeaderRows()}</TableHead>
            <TableBody>{renderBody()}</TableBody>
          </MuiTable>
        </div>
      </div>
    </>
  );
};

export default AllocationTableUI;
